import {
    CloseCircleTwoTone,
    MinusCircleOutlined,
    MinusCircleTwoTone,
    PlusCircleTwoTone,
  } from "@ant-design/icons";
  import { Divider, Form } from "antd";
  import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
  import TextInput from "components/Inputs/TextInput/TextInput";
  import Selector from "components/Selector/Selector";
  import useMessage from "hooks/useMessage";
  import { t } from "i18next";
  import PageLayout from "layout/PageLayout/PageLayout";
  import { FC, useMemo, useState } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import { getToken } from "redux/features/app/authTokenSlice";
  import { setFeatureData } from "redux/features/app/featuresSettingSlice";
  import centralApi from "services/centralApi";
  import { API_ENDPOINTS } from "utils/constants";
  import { CampaignChannelTypeEnum, TimeUnitEnum } from "utils/enums";
  import "./Budget.css";
  import { currencyOptions } from "helperFunctions/common";
  
  const Budget: FC = () => {
    const loginUser = useSelector((state: any) => state.loginUser.data);
    const { appId } = useSelector((state: any) => state.activeApp);
    const dispatch: any = useDispatch();
  
    const budget = useSelector(
      (state: any) => state.featuresSetting?.data[0]?.budget
    );
    const [formData, setFormData] = useState(() => {
      if (budget?.length) {
        // Map over DB data to ensure proper structure and mutable objects
        return budget.map((channel) => ({
          channel: channel.channel || "",
          providers: (channel.providers || []).map((provider) => ({
            providerId: provider.providerId || "",
            default: provider.default ?? false,
            costPerMessage: provider.costPerMessage || "",
            currency: provider.currency || "",
            totalCostLimit: {
              count: provider.totalCostLimit?.count || "",
              timeWindow: provider.totalCostLimit?.timeWindow || "",
            },
            totalMessageCountLimit: {
              count: provider.totalMessageCountLimit?.count || "",
              timeWindow: provider.totalMessageCountLimit?.timeWindow || "",
            },
          })),
        }));
      }
  
      // Fallback initial empty structure
      return [
        {
          channel: "",
          providers: [
            {
              providerId: "",
              default: false,
              costPerMessage: "",
              currency: "",
              totalCostLimit: { count: "", timeWindow: "" },
              totalMessageCountLimit: { count: "", timeWindow: "" },
            },
          ],
        },
      ];
    });
  
    const handleFormChange = (
      chIdx: number,
      provIdx: number | null, // null if updating channel itself
      key: string,
      value: any
    ) => {
      setFormData((prev) => {
        const updated = [...prev]; // shallow copy of channels
  
        if (provIdx === null) {
          // Update channel-level field
          updated[chIdx] = {
            ...updated[chIdx],
            [key]: value,
            // Reset providers when channel changes
            providers: [
              {
                providerId: "",
                default: false,
                costPerMessage: "",
                currency: "",
                totalCostLimit: { count: "", timeWindow: "" },
                totalMessageCountLimit: { count: "", timeWindow: "" },
              },
            ],
          };
        } else {
          // Update provider-level field safely
          const providers = [...updated[chIdx].providers];
          providers[provIdx] = { ...providers[provIdx], [key]: value };
          updated[chIdx] = { ...updated[chIdx], providers };
        }
  
        return updated;
      });
    };
  
    // Add new provider under a channel
    const addProvider = (chIdx: number) => {
      const updated = [...formData];
      updated[chIdx].providers.push({
        providerId: "",
        default: false,
        costPerMessage: "",
        currency: "",
        totalCostLimit: { count: "", timeWindow: "" },
        totalMessageCountLimit: { count: "", timeWindow: "" },
      });
      setFormData(updated);
    };
  
    // Remove provider
    const removeProvider = (chIdx: number, provIdx: number) => {
      const updated = [...formData];
      updated[chIdx].providers.splice(provIdx, 1);
      setFormData(updated);
    };
  
    // Add new channel
    const addChannel = () => {
      setFormData([
        ...formData,
        {
          channel: "",
          providers: [
            {
              providerId: "",
              default: false,
              costPerMessage: "",
              currency: "",
              totalCostLimit: { count: "", timeWindow: "" },
              totalMessageCountLimit: { count: "", timeWindow: "" },
            },
          ],
        },
      ]);
    };
  
    const { showError, showSuccess } = useMessage();
  
    const handleSubmit = async () => {
      try {
        const authToken = await dispatch(getToken()).unwrap();
        const payload = {
          args: { budget: formData },
          app_id: appId,
          api_key: loginUser.api_key,
          authToken,
        };
        await centralApi("POST", API_ENDPOINTS.SAVE_APP_SETTING_URL, payload);
  
        dispatch(setFeatureData({ budget: formData }));
        showSuccess(t("sucessfullySavedLabel"));
      } catch (error) {
        showError(t("somethingWrongLabel"));
      }
    };
  
    const features = useSelector((state: any) => state.featuresSetting.data);
    const channelStatus = features[0]?.channel_status;
    const partnersData = features[0]?.partnerInfo?.partnerData || [];
    console.log("partner data", partnersData);
    const channelOptions = useMemo(() => {
      const allChannels = Object.values(CampaignChannelTypeEnum);
      if (!channelStatus) {
        return allChannels
          .filter((c) => c !== CampaignChannelTypeEnum.ALL)
          .map((c) => ({ value: c, label: c }));
      }
      return allChannels
        .filter((c) => c !== CampaignChannelTypeEnum.ALL && channelStatus[c])
        .map((c) => ({ value: c, label: c }));
    }, [channelStatus]);
  
    const providerOptions = [
      { label: "Provider A", value: "providerA" },
      { label: "Provider B", value: "providerB" },
    ];
    const addBudgetBlock = () => {
      setFormData([
        ...formData,
        {
          channel: "",
          providers: [
            {
              providerId: "",
              default: false,
              costPerMessage: "",
              currency: "",
              totalCostLimit: { count: "", timeWindow: "" },
              totalMessageCountLimit: { count: "", timeWindow: "" },
            },
          ],
        },
      ]);
    };
  
    // Remove channel
    const removeChannel = (chIdx: number) => {
      setFormData(formData.filter((_, i) => i !== chIdx));
    };
    console.log("formaa", formData);
    return (
      <PageLayout>
        <div className="mb-4">
          <h6 className="file-encryption-setting-container-heading">
            {t("budgetSettingLabel")}
          </h6>
          <Divider className="file-encryption-setting-custom-divider" />
        </div>
        <div className="">
          <Form
            onFinish={handleSubmit}
            layout="horizontal"
            labelCol={{ flex: "170px" }}
            labelAlign="left"
            className="col-sm-12"
          >
            <div className="d-flex flex-wrap gap-3">
              {formData.map((channelEntry, chIdx) => (
                <div
                  key={chIdx}
                  className=" w-100 mb-6  p-3 border rounded position-relative"
                >
                  {formData.length > 1 && (
                    <CloseCircleTwoTone
                      onClick={() => removeChannel(chIdx)}
                      className="cursor-pointer position-absolute remove-channel-btn"
                    />
                  )}
                  {/* Channel Selector */}
                  <Form.Item
                    labelCol={{ flex: "100px" }}
                    label={t("selectChannelLabel")}
                    className="w-25"
                  >
                    <Selector
                      placeholder={t("selectChannelLabel")}
                      value={channelEntry.channel}
                      onChange={(val) =>
                        handleFormChange(chIdx, null, "channel", val)
                      }
                      options={channelOptions}
                    />
                  </Form.Item>
  
                  {/* Providers inside channel */}
                  <div className="w-100 d-flex flex-wrap gap-3">
                    {channelEntry.providers.map((prov, provIdx) => (
                      <div
                        key={provIdx}
                        className="budget-provider-card p-2 mb-2 border rounded" // 3 per row
                      >
                        <div className="d-flex justify-content-end mb-2 ">
                          <CloseCircleTwoTone
                            onClick={() => removeProvider(chIdx, provIdx)}
                            className=" cursor-pointer "
                          />
                        </div>
  
                        <Form.Item label={t("providerLabel")} className="w-100">
                          <Selector
                            placeholder="Select provider"
                            value={prov.providerId}
                            onChange={(val) => {
                              handleFormChange(chIdx, provIdx, "providerId", val);
                              const providerInfo = (partnersData || []).find(
                                (p: any) => p.partnerId === val
                              );
                              handleFormChange(
                                chIdx,
                                provIdx,
                                "default",
                                providerInfo?.default ?? false
                              );
                            }}
                            options={(partnersData || [])
                              .filter(
                                (p: any) => p.channel === channelEntry.channel
                              )
                              .map((p: any) => ({
                                value: p.partnerId,
                                label: p.partnerName,
                              }))}
                          />
                        </Form.Item>
  
                        {/* Cost + Currency */}
                        <div className="d-flex gap-2">
                          <Form.Item
                            label={t("costPerMessageLabel")}
                            className="w-100"
                          >
                            <TextInput
                              type="numeric"
                              value={prov.costPerMessage}
                              onChange={(e) =>
                                handleFormChange(
                                  chIdx,
                                  provIdx,
                                  "costPerMessage",
                                  e.target.value
                                )
                              }
                            />
                          </Form.Item>
  
                          <Form.Item
                            label={t("currencyLabel")}
                            labelCol={{ flex: "80px" }}
                            className="w-100"
                          >
                            <Selector
                              placeholder={t("selectCurrencyLabel")}
                              value={prov.currency}
                              onChange={(val) =>
                                handleFormChange(chIdx, provIdx, "currency", val)
                              }
                              options={currencyOptions}
                            />
                          </Form.Item>
                        </div>
  
                        {/* Total Cost Limit */}
                        <div className="d-flex gap-2">
                          <Form.Item
                            label={t("totalCostLimitLabel")}
                            className="w-100"
                          >
                            <TextInput
                              type="numeric"
                              value={prov.totalCostLimit.count}
                              onChange={(e) =>
                                handleFormChange(
                                  chIdx,
                                  provIdx,
                                  "totalCostLimit",
                                  {
                                    ...prov.totalCostLimit,
                                    count: e.target.value,
                                  }
                                )
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            label={t("perLabel")}
                            labelCol={{ flex: "80px" }}
                            className="w-100"
                          >
                            <Selector
                              placeholder={t("selectLabel")}
                              value={prov.totalCostLimit.timeWindow}
                              onChange={(val) =>
                                handleFormChange(
                                  chIdx,
                                  provIdx,
                                  "totalCostLimit",
                                  { ...prov.totalCostLimit, timeWindow: val }
                                )
                              }
                              options={[
                                {
                                  label: t("dayLabel"),
                                  value: TimeUnitEnum.DAYS,
                                },
                                {
                                  label: t("weekLabel"),
                                  value: TimeUnitEnum.WEEKS,
                                },
                                {
                                  label: t("hoursLabel"),
                                  value: TimeUnitEnum.HOURS,
                                },
                                {
                                  label: t("monthLabel"),
                                  value: TimeUnitEnum.MONTHS,
                                },
                              ]}
                            />
                          </Form.Item>
                        </div>
  
                        {/* Total Message Count Limit */}
                        <div className="d-flex gap-2">
                          <Form.Item
                            label={t("totalMessageCountLimitLabel")}
                            className="w-100"
                          >
                            <TextInput
                              type="numeric"
                              value={prov.totalMessageCountLimit.count}
                              onChange={(e) =>
                                handleFormChange(
                                  chIdx,
                                  provIdx,
                                  "totalMessageCountLimit",
                                  {
                                    ...prov.totalMessageCountLimit,
                                    count: e.target.value,
                                  }
                                )
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            label="per"
                            labelCol={{ flex: "80px" }}
                            className="w-100"
                          >
                            <Selector
                              placeholder={t("selectLabel")}
                              value={prov.totalMessageCountLimit.timeWindow}
                              onChange={(val) =>
                                handleFormChange(
                                  chIdx,
                                  provIdx,
                                  "totalMessageCountLimit",
                                  {
                                    ...prov.totalMessageCountLimit,
                                    timeWindow: val,
                                  }
                                )
                              }
                              options={[
                                {
                                  label: t("dayLabel"),
                                  value: TimeUnitEnum.DAYS,
                                },
                                {
                                  label: t("weekLabel"),
                                  value: TimeUnitEnum.WEEKS,
                                },
                                {
                                  label: t("hoursLabel"),
                                  value: TimeUnitEnum.HOURS,
                                },
                                {
                                  label: t("monthLabel"),
                                  value: TimeUnitEnum.MONTHS,
                                },
                              ]}
                            />
                          </Form.Item>
                        </div>
  
                        {/* Remove provider */}
                      </div>
                    ))}
                  </div>
               
                  <div className="d-flex gap-2 mt-2">
                    <PrimaryButton
                      type="dashed"
                      onClick={() => addProvider(chIdx)}
                    >
                      + {t("addProviderLabel")}
                    </PrimaryButton>
                  </div>
                </div>
              ))}
            </div>
            {/* Add channel button */}
            <PrimaryButton className="mt-3" type="primary" onClick={addChannel}>
             + {t("addChannelLabel")}
            </PrimaryButton>
  
            <PrimaryButton type="primary" htmlType="submit" className="ms-2">
              {t("saveLabel")}
            </PrimaryButton>
          </Form>
        </div>
      </PageLayout>
    );
  };
  
  export default Budget;
  